import React , { useEffect, useMemo, useRef, useState } from 'react';

import { duplicateConfig } from '../../../config/agentic/duplicateConfig';
import {
    getFieldMatchLevel,
    getReportTitle,
    mapApiDuplicatesToTableRows,
    mapInputRecordToPrimaryRow,
    extractDuplicateComparableFields,
} from '../../../utils/helper';
import ConfirmDialog from "../ConfirmDialog.tsx";
import ReportLegend from '../ReportLegend';
import ReportSummary from '../ReportSummary';
import DuplicateSection from '../DuplicateSection';
import EmptyBenefitsState from '../EmptyBenefitsState.jsx';
import { toast, ToastContainer } from "react-toastify";
import { updateRecordRole } from '../../../api/agentic/duplicateApi.js';

const IntakeAgentReport = React.memo(({ reportData }) => {
    const [potentialDuplicates, setPotentialDuplicates] = useState([]);
    const [primaryApplications, setPrimaryApplications] = useState([]);
    const [duplicateApplications, setDuplicateApplications] = useState([]);
    const [reportConfig, setReportConfig] = useState({
        legendTitle: '',
        columns: duplicateConfig.getColumns({ isBenefitsAgent: false, showAction: true }),
    });
  const duplicateComparable = extractDuplicateComparableFields(potentialDuplicates);
  const [selectedPrimary, setSelectedPrimary] = useState(null);

    const [openDialog, setOpenDialog] = useState(false);
    const [currentRow, setCurrentRow] = useState(null);
    const [isDuplicate, setIsDuplicate] = useState(false);
    const tagPrimaryInFlightRef = useRef(false);

    const isTagPrimaryBusy = () => {
        if (tagPrimaryInFlightRef.current) {
            toast.info("Tag as primary is already in progress.");
            return true;
        }
        return false;
    };

    const dedupeByCaseId = (rows = []) => {
        const seen = new Set();
        return rows.filter((row) => {
            const key = row?.caseId;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    const mapDemotedPrimaryRow = (oldPrimaryCaseId, oldPrimaryRow) => {
        if (!oldPrimaryCaseId) return null;

        const matchedOldPrimary = reportData?.matches?.find(
            (match) => match.case_id === oldPrimaryCaseId,
        );

        if (matchedOldPrimary) {
            const mapped = mapApiDuplicatesToTableRows([matchedOldPrimary]);
            if (mapped?.length) return mapped[0];
        }

        return oldPrimaryRow
            ? {
                caseId: oldPrimaryRow.caseId,
                submitDate: oldPrimaryRow.submitDate,
                origin: oldPrimaryRow.origin,
                firstName: oldPrimaryRow.firstName,
                lastName: oldPrimaryRow.lastName,
                caseManager: oldPrimaryRow.caseManager,
                notes: oldPrimaryRow.notes,
                status: oldPrimaryRow.status || "-",
            }
            : null;
    };

    useEffect(() => {
        if (!reportData?.input_record) return;

        const mappedPrimary = mapInputRecordToPrimaryRow(reportData.input_record, duplicateComparable);

        setPrimaryApplications([mappedPrimary]);
    }, [reportData?.input_record]);

    useEffect(() => {
        if (!reportData) return;

        const matches = reportData?.matches
            || reportData?.output_text?.matches
            || reportData?.output_text?.data_sources_matches || [];
        const statusToSetter = {
            POTENTIAL_DUPLICATE: setPotentialDuplicates,
            PRIMARY: setPrimaryApplications,
            DUPLICATE: setDuplicateApplications,
        };

        Object.entries(statusToSetter).forEach(([status, setter]) => {
            const caseIds = Object.entries(reportData?.outputText?.case_status || reportData?.case_status || {})
                .filter(([_, caseStatus]) => caseStatus === status)
                .map(([caseId]) => caseId);

            const filteredMatches = matches.filter((match) => caseIds.includes(match.case_id));

            let mappedRows;

            if (status === "PRIMARY") {
                // For PRIMARY, use the first record or fallback to input_record
                const primaryRecord = filteredMatches[0]?.record?.full_candidate_record || reportData?.input_record;
                mappedRows = primaryRecord
                    ? [mapInputRecordToPrimaryRow(primaryRecord, duplicateComparable)]
                    : [];
            } else {
                // For non-PRIMARY statuses
                mappedRows = mapApiDuplicatesToTableRows(filteredMatches) || [];

                // Ensure input_record is added if its CaseID is in caseIds and it's not already in mappedRows
                const inputRecordCaseID = reportData?.input_record?.CaseID;

                const isCaseIdInCaseIds = caseIds.includes(inputRecordCaseID);

                const isAlreadyInMappedRows = mappedRows.some(
                    (row) => row.CaseID === inputRecordCaseID,
                );

                if (isCaseIdInCaseIds && !isAlreadyInMappedRows) {
                    // Push input_record as a table row
                    mappedRows.push(...mapApiDuplicatesToTableRows([{ raw: reportData.input_record }]));
                }
            }

            setter(mappedRows);
        });
    }, [reportData]);
    // If no reportData or summary, render nothing
    if (!reportData || !reportData.summary) return null;


    useEffect(() => {
        const title = getReportTitle({ reportType: "DUPLICATE", reportData });

        setReportConfig((prev) => ({
            ...prev,
            legendTitle: title,
        }));
    }, [reportData]);

    const handleMarkDuplicate = async (selectedRows) => {
        try {
            await Promise.all(
                selectedRows.map((row) => updateRecordRole({
                    caseId: row.caseId,
                    recordRole: "DUPLICATE",
                })),
            );

            setDuplicateApplications((prev) => {
                const newRows = selectedRows.filter(
                    (selected) =>
                        !prev.some((existing) => existing.caseId === selected.caseId),
                );

                return [...prev, ...newRows];
            });

            setPotentialDuplicates((prev) => prev.filter(
                (row) => !selectedRows.some(
                    (selected) => selected.caseId === row.caseId,
                ),
            ));
            toast.success("Application marked as duplicate successfully");
        } catch (error) {
            console.error("Failed to mark duplicate:", error);
            toast.error("Failed to mark duplicate. Please try again.");
        }
    };

    const handleTagPrimary = async (row, isDuplicate = true) => {
        try {
            if (isTagPrimaryBusy()) return;
            if (primaryApplications?.length > 0) {
                setCurrentRow(row);
                setOpenDialog(true);
                setIsDuplicate(isDuplicate);
            }
        } catch (error) {
            console.error("Failed to tag primary:", error);
            toast.error("Failed to mark as primary.");
        }
    };

    const proceedWithTagging = async (row) => {
        if (isTagPrimaryBusy()) return;
        tagPrimaryInFlightRef.current = true;

        const oldPrimaryRow = primaryApplications[0] || null;
        const oldPrimaryCaseId = oldPrimaryRow?.caseId;

        try {
            await updateRecordRole({
                caseId: row.caseId,
                recordRole: "PRIMARY",
            });

            if (oldPrimaryCaseId && oldPrimaryCaseId !== row.caseId) {
                await updateRecordRole({
                    caseId: oldPrimaryCaseId,
                    recordRole: isDuplicate ? "DUPLICATE" : "POTENTIAL_DUPLICATE",
                });
            }

            let primaryRow;

            const fullRecordMatch = reportData?.matches?.find(
                (m) => m.record?.full_candidate_record?.CaseID === row.caseId,
            );

            if (fullRecordMatch?.record?.full_candidate_record) {
                primaryRow = mapInputRecordToPrimaryRow(
                    fullRecordMatch.record.full_candidate_record,
                    duplicateComparable,
                );
            } else if (
                reportData?.input_record
                && reportData.input_record.CaseID === row.caseId
            ) {
                primaryRow = mapInputRecordToPrimaryRow(
                    reportData.input_record,
                    duplicateComparable,
                );
            } else {
                primaryRow = {
                    caseId: row.caseId,
                    submitDate: row.submitDate,
                    origin: row.origin,
                    firstName: row.firstName,
                    lastName: row.lastName,
                    caseManager: row.caseManager,
                    notes: "Tagged as Primary from duplicates",
                    details: [],
                };
            }
            const demotedPrimary = oldPrimaryCaseId && oldPrimaryCaseId !== row.caseId
                ? mapDemotedPrimaryRow(oldPrimaryCaseId, oldPrimaryRow)
                : null;

            if (isDuplicate) {
                setDuplicateApplications((prev) => dedupeByCaseId([
                    ...dedupeByCaseId(prev).filter(
                        (item) => item.caseId !== row.caseId && item.caseId !== oldPrimaryCaseId,
                    ),
                    ...(demotedPrimary ? [demotedPrimary] : []),
                ]));
                setPotentialDuplicates((prev) => dedupeByCaseId(
                    prev.filter((item) => item.caseId !== row.caseId && item.caseId !== oldPrimaryCaseId),
                ));
            } else {
                setPotentialDuplicates((prev) => dedupeByCaseId([
                    ...dedupeByCaseId(prev).filter(
                        (item) => item.caseId !== row.caseId && item.caseId !== oldPrimaryCaseId,
                    ),
                    ...(demotedPrimary ? [demotedPrimary] : []),
                ]));
                setDuplicateApplications((prev) => dedupeByCaseId(
                    prev.filter((item) => item.caseId !== row.caseId && item.caseId !== oldPrimaryCaseId),
                ));
            }
            setPrimaryApplications((prev) => {
                const exists = prev.some((item) => item.caseId === primaryRow.caseId);

                if (exists) return prev;
                return [primaryRow];
            });

            setSelectedPrimary(primaryRow);
            toast.success("Marked as primary successfully!");
        } catch (error) {
            console.error("Failed to tag primary:", error);
            toast.error("Failed to mark as primary.");
        } finally {
            tagPrimaryInFlightRef.current = false;
        }
    };

    // Confirm Dialog Close Handler
    const handleDialogClose = () => {
        setOpenDialog(false); // Close the dialog without doing anything
    };

    // Confirm Dialog Confirm Handler (Execute the tagging after confirmation)
    const handleDialogConfirm = async () => {
        if (isTagPrimaryBusy()) return;

        if (currentRow) {
            // Proceed with tagging the new primary
            await proceedWithTagging(currentRow);

            setOpenDialog(false); // Close the dialog after confirming
        }
    };

const comparedDuplicates = useMemo(() => {
    const primary = primaryApplications[0];
    if (!primary) return potentialDuplicates;

    return potentialDuplicates.map((row) => ({
      ...row,
      _compare: {
        firstName: getFieldMatchLevel(primary.firstName, row.firstName),
        lastName: getFieldMatchLevel(primary.lastName, row.lastName),
      },
    }));
  }, [potentialDuplicates, primaryApplications]);
       
    return (
        <>
            <ReportLegend title={reportConfig.legendTitle} legends />

            <ReportSummary summary={reportData?.summary} />

            <DuplicateSection
                title="Primary Application Details"
                columns={duplicateConfig.primaryApplColumns}
                data={primaryApplications}
                showCheckbox={false}
                emptyComponent={(
                    <EmptyBenefitsState
                        title="No Application Found"
                    />
                )}
            />

            <DuplicateSection
                title="Potential Duplicates"
                columns={reportConfig.columns}
                data={comparedDuplicates}
                onTagPrimary={(row) => handleTagPrimary(row, false)}
                onMarkDuplicate={handleMarkDuplicate}
                showActions
                actionLabel="Mark as Duplicate"
                emptyComponent={(
                    <EmptyBenefitsState
                        title="No Potential Duplicates"
                    />
                )}
            />

            <DuplicateSection
                title="Duplicate Applications"
                columns={reportConfig.columns}
                data={duplicateApplications}
                onTagPrimary={(row) => handleTagPrimary(row, true)}
                emptyComponent={(
                    <EmptyBenefitsState
                        title="No Duplicate Applications"
                    />
                )}
            />
            <ConfirmDialog
                open={openDialog}
                title="Set as Primary Application?"
                message="You already have a primary application. Setting this one as primary will replace the current primary application. Are you sure you want to continue?"
                onConfirm={handleDialogConfirm}
                onClose={handleDialogClose}
                color="primary" // You can change the button color here
            />
        </>
    )
});
export default IntakeAgentReport;
